import currencyFormatter from '~/utils/currencyFormatter';

import type { SimulationResult } from './PromoSimulationResults';

export type SimulationWarning = { scope: string; itemId?: string; title: string; message: string };
const MONEY_TOLERANCE = 0.01;

export function hasItemTotalMismatch(
  orderTotal: number | null | undefined,
  calculatedTotal: number | null | undefined,
): boolean {
  return (
    Number.isFinite(orderTotal) &&
    Number.isFinite(calculatedTotal) &&
    Math.abs(orderTotal - calculatedTotal) > MONEY_TOLERANCE
  );
}

type CalculatedItem = SimulationResult['promo']['items'][number];

function channelMarkupIds(item?: App.AnyItem): string {
  if (!item) return '-';
  const values = ['channel_markup_id', 'channelMarkup_ids', 'channelMarkupId', 'channelMarkupIds'].flatMap((key) => {
    const value: unknown = item[key];
    return Array.isArray(value) ? value : [value];
  });
  return (
    Array.from(
      new Set(values.filter((value): value is string => typeof value === 'string' && Boolean(value.trim()))),
    ).join(', ') || '-'
  );
}

export function buildSimulationRows(
  order: App.Order | null,
  items: Record<string, App.AnyItem>,
  result: { promo: Pick<SimulationResult['promo'], 'currency' | 'items'> } | null,
  warnings: Array<SimulationWarning>,
) {
  const warningRows = [...warnings];
  const simulatedItems = result?.promo.items ?? [];
  const matchedItems = new Set<CalculatedItem>();
  const sameCurrency = !order || !result || order.currency_code === result.promo.currency;
  if (!sameCurrency) {
    warningRows.push({
      scope: 'Order',
      title: 'Currency mismatch',
      message: `Order currency ${order.currency_code} differs from calculation currency ${result.promo.currency}; discounts cannot be compared.`,
    });
  }
  const entries: Array<{ itemId: string; orderItem?: App.AnyItem; calculatedItem?: CalculatedItem }> = Object.entries(
    items,
  ).map(([itemId, orderItem]) => {
    const transactionKey: unknown = orderItem.transaction_key;
    // Prefer the contract's transaction key; fall back to the order item ID for older responses.
    const calculatedItem =
      simulatedItems.find(
        (item) => !matchedItems.has(item) && transactionKey && item.transactionKey === transactionKey,
      ) ?? simulatedItems.find((item) => !matchedItems.has(item) && item.itemId === itemId);
    if (calculatedItem) matchedItems.add(calculatedItem);
    return { itemId, orderItem, calculatedItem };
  });
  simulatedItems.forEach((item, index) => {
    if (!matchedItems.has(item))
      entries.push({
        itemId: item.itemId || item.transactionKey || `calculator-item-${index + 1}`,
        calculatedItem: item,
      });
  });
  const rows = entries.map(({ itemId, orderItem, calculatedItem }, index) => {
    const itemWarnings: Array<SimulationWarning> = [];
    const addWarning = (title: string, message: string) => itemWarnings.push({ scope: 'Item', itemId, title, message });
    if (calculatedItem?.warning || calculatedItem?.warningCode)
      addWarning(
        calculatedItem.warningCode || 'Calculator warning',
        calculatedItem.warning || calculatedItem.warningCode,
      );
    if (orderItem && result && !calculatedItem)
      addWarning('Missing calculated item', 'Order item was not returned by the calculator.');
    if (!orderItem && order && calculatedItem)
      addWarning('Unmatched calculated item', 'Calculated item could not be matched to an order item.');
    const recordedPromo = orderItem
      ? (orderItem.promo_code_discount_amount ?? 0)
      : calculatedItem?.discountResult?.promo_code_discount_amount;
    const recordedVoucher = orderItem
      ? (orderItem.voucher_discount_amount ?? 0)
      : calculatedItem?.discountResult?.voucher_discount_amount;
    const simulatedPromo = calculatedItem?.discountAmount;
    const simulatedVoucher = calculatedItem
      ? (calculatedItem.vouchersApplied ?? []).reduce((sum, voucher) => sum + voucher.amount, 0)
      : undefined;
    const itemTotalMismatch = sameCurrency && hasItemTotalMismatch(orderItem?.total, calculatedItem?.discountableTotal);
    if (itemTotalMismatch) {
      addWarning(
        'Item price mismatch',
        `Order item price: ${orderItem.total}; calculated discountable price: ${calculatedItem.discountableTotal}.`,
      );
    }
    if (sameCurrency && calculatedItem) {
      if (recordedPromo != null && simulatedPromo != null && Math.abs(recordedPromo - simulatedPromo) > MONEY_TOLERANCE)
        addWarning('Promo discount mismatch', `Recorded: ${recordedPromo}; simulated: ${simulatedPromo}.`);
      if (
        recordedVoucher != null &&
        simulatedVoucher != null &&
        Math.abs(recordedVoucher - simulatedVoucher) > MONEY_TOLERANCE
      )
        addWarning('Voucher discount mismatch', `Recorded: ${recordedVoucher}; simulated: ${simulatedVoucher}.`);
    }
    warningRows.push(...itemWarnings);
    return {
      id: `${order?.id_orders || order?.id || 'simulation'}-${index}`,
      itemId,
      itemType: orderItem
        ? 'fk_custom_offer_id' in orderItem
          ? 'custom_offer'
          : orderItem.type
        : calculatedItem?.categoryBK,
      category: calculatedItem?.categoryBK,
      itemTotalMismatch,
      total: orderItem?.total,
      discountableTotal: calculatedItem?.discountableTotal,
      recordedPromo,
      simulatedPromo,
      recordedVoucher,
      simulatedVoucher,
      creditAmount: calculatedItem?.creditAmount,
      voucherBreakdown: (calculatedItem?.vouchersApplied ?? [])
        .map(
          (voucher) =>
            `${voucher.voucherName || voucher.voucherTitle || voucher.voucherId}: ${currencyFormatter(voucher.voucherCurrency, voucher.amount, 2)} (remaining ${currencyFormatter(voucher.voucherCurrency, voucher.remainingAmountAfter, 2)})`,
        )
        .join('\n'),
      discountRule: calculatedItem
        ? `${calculatedItem.discountConfigured}${calculatedItem.discountType === 'percentage' ? '%' : ''} (${calculatedItem.discountType})`
        : '-',
      channelMarkupIds: channelMarkupIds(orderItem),
      warnings: itemWarnings.map((warning) => `${warning.title}: ${warning.message}`).join('\n'),
      details: { orderItem, calculatedItem },
    };
  });
  if (order?.payments?.length && Object.keys(items).length) {
    const itemTotal = Object.values(items).reduce((sum, item) => sum + (item.promo_code_discount_amount ?? 0), 0);
    const paymentTotal = order.payments
      .filter((payment) => payment.type === 'promo')
      .reduce((sum, payment) => sum + payment.total, 0);
    if (Math.abs(itemTotal - paymentTotal) > MONEY_TOLERANCE)
      warningRows.push({
        scope: 'Order',
        title: 'Promo payment mismatch',
        message: `Recorded item promo discounts (${itemTotal}) differ from promo payment total (${paymentTotal}).`,
      });
  }
  return { rows, warningRows: warningRows.map((warning, index) => ({ ...warning, id: index })) };
}
